import React, { useEffect } from 'react';
import { Input, type InputRef } from 'antd';
import { faxNanpDigitsForInput } from 'utils/eFaxScheduleRows';
import { buildNanpDisplayMask, caretPosAfterDigits, parseNanpChange } from 'components/form/nanpInputMask';
import s from './s.module.less';

/**
 * US / Canada NANP fax line: 10-digit national number, shown as (555) 123-4567 while typing.
 * `value` may be any API string (+1…); display is normalized to 10 digits. On save use `normalizeFaxForApi`;
 * works with antd `Form` / `AutoComplete` via standard `ChangeEvent` `onChange`.
 */
export type USFaxNumberInputProps = Omit<React.ComponentProps<typeof Input>, 'value' | 'maxLength'> & {
    value?: string;
    needDisplay?: boolean;
    disabled?: boolean;
    customPaddingLeft?: number;
};

export { buildNanpDisplayMask } from 'components/form/nanpInputMask';

const USFaxNumberInput = React.forwardRef<InputRef, USFaxNumberInputProps>((props, ref) => {
    const {
        value,
        onChange,
        size = 'middle',
        needDisplay = true,
        disabled = false,
        customPaddingLeft = 0,
        ...others
    } = props;
    const [val, setVal] = React.useState('');
    const innerRef = React.useRef<InputRef | null>(null);
    const pendingCaret = React.useRef<number | null>(null);

    useEffect(() => {
        setVal(faxNanpDigitsForInput(value));
    }, [value]);

    const format = needDisplay ? buildNanpDisplayMask : (digits: string) => digits;

    React.useLayoutEffect(() => {
        if (pendingCaret.current !== null) {
            innerRef.current?.input?.setSelectionRange(pendingCaret.current, pendingCaret.current);
            pendingCaret.current = null;
        }
    }, [val]);

    const emitChange = (newVal: string, base: React.ChangeEvent<HTMLInputElement>) => {
        if (!onChange) return;
        const synthetic = {
            ...base,
            target: { ...base.target, value: newVal },
            currentTarget: { ...base.currentTarget, value: newVal },
        } as React.ChangeEvent<HTMLInputElement>;
        (onChange as React.ChangeEventHandler<HTMLInputElement>)(synthetic);
    };

    return (
        <div className={s.wrap}>
            <Input
                ref={(node) => {
                    innerRef.current = node;
                    if (typeof ref === 'function') ref(node);
                    else if (ref) (ref as React.MutableRefObject<InputRef | null>).current = node;
                }}
                id="us-fax-number-input"
                size={size}
                {...others}
                inputMode="numeric"
                autoComplete="tel-national"
                onChange={(e) => {
                    const el = e.target as HTMLInputElement;
                    const inputType = (e.nativeEvent as InputEvent | undefined)?.inputType;
                    const { digits, digitsBefore } = parseNanpChange(el, inputType, val);
                    const nextDisplay = format(digits);
                    const caretPos = caretPosAfterDigits(nextDisplay, digitsBefore);
                    if (digits !== val) {
                        pendingCaret.current = caretPos;
                        setVal(digits);
                        emitChange(digits, e);
                    } else {
                        // 数字没变（如输入了字母、删到格式字符边界）时 React 不会重渲染，手动纠正 DOM
                        if (el.value !== nextDisplay) el.value = nextDisplay;
                        el.setSelectionRange(caretPos, caretPos);
                    }
                }}
                className={others.className}
                style={{
                    ...others.style,
                    ...(customPaddingLeft ? { paddingLeft: 11 + customPaddingLeft } : null),
                }}
                value={format(val)}
                disabled={disabled}
            />
        </div>
    );
});

USFaxNumberInput.displayName = 'USFaxNumberInput';

export default USFaxNumberInput;
